"use client";
import { useFilterProvider, useFilterRuntime } from "@/components/filters/FilterDropdown";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/Command";
import { Spinner } from "@/components/ui/Spinner";
import { Check } from "lucide-react";
import React from "react";

export type ComboFilterOption = {
  label: string;
  value: string;
  count?: number | string;
  icon?: React.ReactNode;
  onSelect?: (option: ComboFilterOption) => void;
};

type FilterOptionsLoader = (search: string) => Promise<ComboFilterOption[]>;

const asyncOptionsCache = new WeakMap<FilterOptionsLoader, Map<string, ComboFilterOption[]>>();
const asyncOptionsInFlight = new WeakMap<FilterOptionsLoader, Map<string, Promise<ComboFilterOption[]>>>();
const asyncOptionsCacheByKey = new Map<string, Map<string, ComboFilterOption[]>>();
const asyncOptionsInFlightByKey = new Map<string, Map<string, Promise<ComboFilterOption[]>>>();

function getSearchKey(search: string) {
  return search.trim();
}

function getWeakLoaderCache<T>(
  cache: WeakMap<FilterOptionsLoader, Map<string, T>>,
  loader: FilterOptionsLoader,
) {
  let loaderCache = cache.get(loader);
  if (!loaderCache) {
    loaderCache = new Map();
    cache.set(loader, loaderCache);
  }

  return loaderCache;
}

function getKeyedLoaderCache<T>(
  cache: Map<string, Map<string, T>>,
  cacheKey: string,
) {
  let loaderCache = cache.get(cacheKey);
  if (!loaderCache) {
    loaderCache = new Map();
    cache.set(cacheKey, loaderCache);
  }

  return loaderCache;
}

function getResultCache(loader: FilterOptionsLoader, cacheKey: string | undefined) {
  return cacheKey
    ? getKeyedLoaderCache(asyncOptionsCacheByKey, cacheKey)
    : getWeakLoaderCache(asyncOptionsCache, loader);
}

function getInFlightCache(loader: FilterOptionsLoader, cacheKey: string | undefined) {
  return cacheKey
    ? getKeyedLoaderCache(asyncOptionsInFlightByKey, cacheKey)
    : getWeakLoaderCache(asyncOptionsInFlight, loader);
}

async function loadFilterOptions(
  loader: FilterOptionsLoader,
  search: string,
  cacheKey: string | undefined,
) {
  const searchKey = getSearchKey(search);
  const resultCache = getResultCache(loader, cacheKey);
  const cachedOptions = resultCache.get(searchKey);
  if (cachedOptions) {
    return cachedOptions;
  }

  const inFlightCache = getInFlightCache(loader, cacheKey);
  const existingRequest = inFlightCache.get(searchKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = loader(searchKey).then((loadedOptions) => {
    resultCache.set(searchKey, loadedOptions);
    return loadedOptions;
  }).finally(() => {
    inFlightCache.delete(searchKey);
  });

  inFlightCache.set(searchKey, request);
  return request;
}

type FilterComboboxBaseProps = {
  cacheKey?: string;
  selected: string[] | string;
  onChange: (value: string[] | string) => void;
  type: "multi" | "single";
  onSearchChange?: (search: string) => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
};

type AsyncFilterComboboxProps = FilterComboboxBaseProps & {
  defaultOptions?: ComboFilterOption[];
  options?: never;
  isLoading?: never;
  getOptions: FilterOptionsLoader;
  getOptionById?: (value: string) => Promise<ComboFilterOption | null>;
  getOptionsByIds?: (values: string[]) => Promise<ComboFilterOption[]>;
};

type StaticFilterComboboxProps = FilterComboboxBaseProps & {
  defaultOptions?: never;
  options: ComboFilterOption[];
  isLoading: boolean;
  getOptions?: never;
  getOptionById?: never;
  getOptionsByIds?: never;
};

type FilterComboboxProps = AsyncFilterComboboxProps | StaticFilterComboboxProps;

export function FilterCombobox({
  defaultOptions,
  options: staticOptions,
  onChange,
  type,
  selected,
  isLoading: staticLoading,
  getOptions,
  getOptionById,
  getOptionsByIds,
  cacheKey,
  onSearchChange,
  onLoadMore,
  isLoadingMore,
}: FilterComboboxProps) {
  const { setOpenFilterValue } = useFilterProvider();
  const runtime = useFilterRuntime();
  const runtimeFilterKey = runtime?.filterKey;
  const requestClose = runtime?.requestClose;
  const onOptionsResolved = runtime?.onOptionsResolved;
  const resolvedCacheKey = cacheKey ?? runtimeFilterKey;
  const getOptionsRef = React.useRef(getOptions);
  const getOptionByIdRef = React.useRef(getOptionById);
  const getOptionsByIdsRef = React.useRef(getOptionsByIds);
  const onOptionsResolvedRef = React.useRef(onOptionsResolved);
  getOptionsRef.current = getOptions;
  getOptionByIdRef.current = getOptionById;
  getOptionsByIdsRef.current = getOptionsByIds;
  onOptionsResolvedRef.current = onOptionsResolved;

  // State for async mode
  const [options, setOptions] = React.useState<ComboFilterOption[]>(
    staticOptions || defaultOptions || [],
  );
  const [internalLoading, setInternalLoading] = React.useState(false);

  // Determine if we're in async mode
  const isAsync = !!getOptions;
  const isLoading = isAsync ? internalLoading : staticLoading;

  // Cache for options we've fetched
  const optionsCache = React.useRef<Record<string, ComboFilterOption>>({});
  const requestIdRef = React.useRef(0);
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastIssuedSearchRef = React.useRef<string | null>(null);
  const registerOptions = React.useCallback((resolvedOptions: ComboFilterOption[]) => {
    if (!runtimeFilterKey || resolvedOptions.length === 0) {
      return;
    }

    onOptionsResolvedRef.current?.(runtimeFilterKey, resolvedOptions);
  }, [runtimeFilterKey]);

  // Initialize options cache when staticOptions change
  React.useEffect(() => {
    if (staticOptions) {
      staticOptions.forEach((option) => {
        optionsCache.current[option.value] = option;
      });
      registerOptions(staticOptions);
      setOptions(staticOptions);
    }
  }, [registerOptions, staticOptions]);

  React.useEffect(() => {
    if (!defaultOptions || defaultOptions.length === 0) {
      return;
    }

    defaultOptions.forEach((option) => {
      optionsCache.current[option.value] = option;
    });
    registerOptions(defaultOptions);
    setOptions((prev) => {
      const nextOptions = [...prev];

      defaultOptions.forEach((option) => {
        if (!nextOptions.some((existingOption) => existingOption.value === option.value)) {
          nextOptions.push(option);
        }
      });

      return nextOptions;
    });
  }, [defaultOptions, registerOptions]);

  const selectedValues = React.useMemo(() => {
    if (!selected) {
      return [];
    }

    if (Array.isArray(selected)) {
      return selected.filter((value): value is string => typeof value === "string" && value.trim().length > 0);
    }

    return typeof selected === "string" && selected.trim().length > 0
      ? [selected]
      : [];
  }, [selected]);

  // Fetch selected options by ID if they are not in cache (async mode only)
  React.useEffect(() => {
    const selectedOptionLoader = getOptionByIdRef.current;
    const selectedOptionsLoader = getOptionsByIdsRef.current;
    if ((!selectedOptionLoader && !selectedOptionsLoader) || !isAsync || selectedValues.length === 0) return;

    const cachedOptions = selectedValues.flatMap((selectedValue) => {
      const cachedOption = optionsCache.current[selectedValue];
      return cachedOption ? [cachedOption] : [];
    });
    const missingValues = selectedValues.filter((selectedValue) => !optionsCache.current[selectedValue]);

    registerOptions(cachedOptions);

    if (missingValues.length === 0) {
      return;
    }

    let isCancelled = false;

    const fetchOptionsIfNeeded = async () => {
      setInternalLoading(true);
      try {
        let loadedOptions: ComboFilterOption[];
        try {
          loadedOptions = selectedOptionsLoader
            ? await selectedOptionsLoader(missingValues)
            : (await Promise.all(
              missingValues.map(async (selectedValue) => {
                try {
                  return await selectedOptionLoader?.(selectedValue) ?? null;
                } catch (error) {
                  console.error("Error fetching option by ID:", error);
                  return null;
                }
              }),
            )).filter((option): option is ComboFilterOption => option !== null);
        } catch (error) {
          console.error("Error fetching options by ID:", error);
          return;
        }

        if (isCancelled || loadedOptions.length === 0) {
          return;
        }

        loadedOptions.forEach((option) => {
          optionsCache.current[option.value] = option;
        });
        registerOptions(loadedOptions);
        setOptions((prev) => {
          const nextOptions = [...prev];

          loadedOptions.forEach((option) => {
            if (!nextOptions.some((existingOption) => existingOption.value === option.value)) {
              nextOptions.push(option);
            }
          });

          return nextOptions;
        });
      } finally {
        if (!isCancelled) {
          setInternalLoading(false);
        }
      }
    };

    void fetchOptionsIfNeeded();

    return () => {
      isCancelled = true;
    };
  }, [isAsync, registerOptions, selectedValues]);

  const handleSearch = React.useCallback(
    async (search: string) => {
      const optionsLoader = getOptionsRef.current;
      if (!optionsLoader) return;

      const searchKey = getSearchKey(search);
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      const cachedOptions = getResultCache(optionsLoader, resolvedCacheKey).get(searchKey);
      if (cachedOptions) {
        setInternalLoading(false);
        setOptions(cachedOptions);
        cachedOptions.forEach((option) => {
          optionsCache.current[option.value] = option;
        });
        registerOptions(cachedOptions);
        return;
      }

      setInternalLoading(true);
      try {
        const newOptions = await loadFilterOptions(optionsLoader, searchKey, resolvedCacheKey);
        if (requestId !== requestIdRef.current) return;

        setOptions(newOptions);
        newOptions.forEach((option) => {
          optionsCache.current[option.value] = option;
        });
        registerOptions(newOptions);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          console.error("Error fetching options:", error);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setInternalLoading(false);
        }
      }
    },
    [registerOptions, resolvedCacheKey],
  );

  // Debounced search handler for async mode
  const debouncedHandleSearch = React.useCallback(
    (search: string) => {
      if (!isAsync) return;

      const searchKey = getSearchKey(search);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      searchDebounceRef.current = setTimeout(() => {
        searchDebounceRef.current = null;
        if (lastIssuedSearchRef.current === searchKey) return;

        lastIssuedSearchRef.current = searchKey;
        void handleSearch(searchKey);
      }, 100);
    },
    [isAsync, handleSearch],
  );

  // Fetch initial options when component mounts (async mode only)
  const hasSearchedRef = React.useRef(false);
  React.useEffect(() => {
    if (isAsync && !hasSearchedRef.current) {
      hasSearchedRef.current = true;
      lastIssuedSearchRef.current = "";
      void handleSearch("");
    }
  }, [isAsync, handleSearch]);

  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const filter = React.useCallback(
    (value: string, search: string) => {
      if (isAsync || onSearchChange) {
        // For async mode, don't filter since we're doing server-side search
        return 1;
      }

      const filteredOption = options?.find((opt) => opt.value === value);
      if (!filteredOption || !search) return 1;

      search = search.toLowerCase();
      const label = filteredOption.label.toLowerCase();

      if (label === search) return 1;
      if (label.startsWith(search)) return 0.8;
      if (label.includes(search)) return 0.6;

      const searchTerms = search.split(/\s+/);
      const matchCount = searchTerms.filter((term) => label.includes(term)).length;

      return (matchCount / searchTerms.length) * 0.4;
    },
    [options, isAsync, onSearchChange],
  );

  const handleSelect = (optValue: string) => {
    if (!onChange) return;

    if (type === "multi") {
      const currentSelected = (selected as string[]) || [];
      const newValues = currentSelected.includes(optValue)
        ? currentSelected.filter((v) => v !== optValue)
        : [...currentSelected, optValue];
      onChange(newValues);
    } else {
      onChange(optValue);
      requestClose?.();
      setOpenFilterValue(null);
    }
  };

  const isSelected = (optValue: string) => {
    if (!selected) return false;
    return type === "multi"
      ? (selected as string[]).includes(optValue)
      : selected === optValue;
  };

  const formatCount = (count: number | string) => {
    if (typeof count === "number") {
      return count.toLocaleString();
    }

    return count;
  };

  return (
    <Command
      className="filter-menu"
      filter={filter}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          requestClose?.();
          setOpenFilterValue(null);
        }
      }}
    >
      <CommandInput
        placeholder="Filter…"
        onValueChange={onSearchChange ?? (isAsync ? debouncedHandleSearch : undefined)}
        showSearchIcon={false}
        hasBorder={false}
      />
      <CommandList className="filter-option-list">
        {isLoading
          ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size="md" tone="faint" />
            </div>
          )
          : (
            <>
              <CommandEmpty>No option found.</CommandEmpty>
              <CommandGroup>
                {options?.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    className={type === "multi" ? "filter-option-checkbox-item" : undefined}
                    onSelect={() => handleSelect(opt.value)}
                  >
                    {type === "multi"
                      ? (
                        <>
                          <Checkbox
                            checked={isSelected(opt.value)}
                            aria-label={`Toggle ${opt.label}`}
                            onCheckedChange={() => {
                              handleSelect(opt.value);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                          {opt.icon}
                          <span className="flex-1 truncate">{opt.label}</span>
                          {opt.count != null && (
                            <span className="filter-item-count">
                              {formatCount(opt.count)}
                            </span>
                          )}
                        </>
                      )
                      : (
                        <>
                          {opt.icon}
                          <span className="flex-1 truncate">{opt.label}</span>
                          {opt.count != null && (
                            <span className="filter-item-count">
                              {formatCount(opt.count)}
                            </span>
                          )}
                          <Check
                            className={isSelected(opt.value) ? "opacity-100" : "opacity-0"}
                          />
                        </>
                      )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
      </CommandList>
      {onLoadMore && (
        <Button type="button" variant="ghost" disabled={isLoading || isLoadingMore} onClick={onLoadMore}>
          {isLoadingMore ? "Loading…" : "Load more"}
        </Button>
      )}
    </Command>
  );
}
